import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowDown, ArrowLeft, ChevronRight, X, MapPin, Users, Briefcase, Building } from 'lucide-react';
import { caseStudies } from '@/data/caseStudiesData';

const fadeInUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } } };
const stagger = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.12 } } };

function AnimNum({ value }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  const [display, setDisplay] = useState('0');
  const numStr = value.replace(/[^0-9.]/g, '');
  const num = parseFloat(numStr);
  useEffect(() => {
    if (!inView) return;
    let s = 0;
    const step = Math.max(num / 125, 0.1);
    const t = setInterval(() => { s += step; if (s >= num) { setDisplay(value); clearInterval(t); } else { const p = value.match(/^\D*/)?.[0]||''; const sx = value.replace(numStr,'').replace(p,''); if (num>=1e6) setDisplay(p+(s/1e6).toFixed(1)+sx); else if (num>=1e3) setDisplay(p+Math.floor(s).toLocaleString()+sx); else setDisplay(p+Math.floor(s)+sx); } }, 16);
    return () => clearInterval(t);
  }, [inView, num, value, numStr]);
  return <span ref={ref}>{display}</span>;
}

function StickyNav({ sections, activeId }) {
  return (
    <div className="fixed right-8 top-1/2 -translate-y-1/2 z-40 hidden xl:flex flex-col gap-3">
      {sections.map(s => <a key={s.id} href={`#${s.id}`} className={`text-[10px] tracking-widest uppercase text-right transition-all duration-300 ${activeId === s.id ? 'text-foreground font-semibold' : 'text-muted-foreground/50 hover:text-muted-foreground'}`}>{s.label}</a>)}
    </div>
  );
}

function TimelineStep({ step, index, total }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: index * 0.1 }} className="flex flex-col items-center text-center relative">
      <div className="w-12 h-12 rounded-full bg-butter flex items-center justify-center mb-3 relative z-10"><span className="font-serif text-sm font-medium">{String(index + 1).padStart(2, '0')}</span></div>
      <p className="text-sm font-medium tracking-wide">{step}</p>
      {index < total - 1 && <ChevronRight className="h-4 w-4 text-muted-foreground rotate-90 mt-3 md:hidden" />}
    </motion.div>
  );
}

function GallerySlideshow() {
  const study = caseStudies.find(s => s.slug === 'find-your-wave');
  const images = study ? [study.heroImage, ...(study.galleryImages || [])] : [];
  const [idx, setIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  useEffect(() => {
    if (!lightbox) return;
    const h = e => { if (e.key==='Escape') setLightbox(false); if (e.key==='ArrowRight') setIdx(p=>(p+1)%images.length); if (e.key==='ArrowLeft') setIdx(p=>(p-1+images.length)%images.length); };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  }, [lightbox, images.length]);
  // Auto-advance
  useEffect(() => { if (lightbox) return; const t = setInterval(() => setIdx(p => (p+1)%images.length), 5000); return () => clearInterval(t); }, [lightbox, images.length]);
  if (!images.length) return null;
  return (
    <>
      <div className="max-w-5xl mx-auto mb-6">
        <div className="relative bg-white border border-border">
          <AnimatePresence mode="wait">
            <motion.div key={idx} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="cursor-pointer" onClick={() => setLightbox(true)}>
              <img src={images[idx]} alt={`Campaign asset ${idx+1}`} className="w-full h-[55vh] md:h-[65vh] object-contain mx-auto" />
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="flex items-center justify-between mt-4">
          <button onClick={() => setIdx(p=>(p-1+images.length)%images.length)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"><ArrowLeft className="h-5 w-5" /> Previous</button>
          <span className="text-sm text-muted-foreground font-medium tracking-widest">{idx+1} / {images.length}</span>
          <button onClick={() => setIdx(p=>(p+1)%images.length)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">Next <ArrowRight className="h-5 w-5" /></button>
        </div>
      </div>
      <div className="flex gap-2 justify-center flex-wrap max-w-5xl mx-auto">
        {images.slice(0,30).map((img,i) => <button key={i} onClick={() => setIdx(i)} className={`w-14 h-14 overflow-hidden border transition-colors ${idx===i?'border-butter':'border-border hover:border-butter/50'}`}><img src={img} alt="" className="w-full h-full object-cover" loading="lazy" /></button>)}
        {images.length > 30 && <span className="w-14 h-14 flex items-center justify-center text-xs text-muted-foreground border border-border">+{images.length-30}</span>}
      </div>
      <AnimatePresence>
        {lightbox && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={() => setLightbox(false)}>
            <button onClick={() => setLightbox(false)} className="absolute top-6 right-6 text-white/60 hover:text-white z-50"><X className="h-8 w-8" /></button>
            <button onClick={e => { e.stopPropagation(); setIdx(p=>(p-1+images.length)%images.length); }} className="absolute left-6 text-white/60 hover:text-white z-50"><ArrowLeft className="h-8 w-8" /></button>
            <button onClick={e => { e.stopPropagation(); setIdx(p=>(p+1)%images.length); }} className="absolute right-6 text-white/60 hover:text-white z-50"><ArrowRight className="h-8 w-8" /></button>
            <img src={images[idx]} alt="" className="max-w-[90vw] max-h-[85vh] object-contain" onClick={e => e.stopPropagation()} />
            <span className="absolute bottom-6 text-white/40 text-sm tracking-widest">{idx+1} / {images.length}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

const NAV = [
  { id: 'fyw-overview', label: 'Overview' },
  { id: 'fyw-strategy', label: 'Strategy' },
  { id: 'fyw-concept', label: 'Concept' },
  { id: 'fyw-execution', label: 'Execution' },
  { id: 'fyw-results', label: 'Results' },
  { id: 'fyw-gallery', label: 'Gallery' },
  { id: 'fyw-next', label: 'Next' },
];

const SERVICES = ['Brand Strategy','Creative Direction','Destination Marketing','Campaign Development','Digital Advertising','Social Media','Video Production','Photography','Media Buying','Out-of-Home Advertising','Search Marketing','Print Design','Website Creative'];
const PILLARS = [
  { icon: MapPin, title: 'Quality of Life', desc: 'Community, neighborhoods, and everyday Miami Beach living.' },
  { icon: Building, title: 'Urban Oasis', desc: 'Architecture, design, wellness, and sophisticated escapes.' },
  { icon: Users, title: 'Culture Capital', desc: 'Arts, music, history, and creative expression.' },
  { icon: Briefcase, title: 'World-Class Experiences', desc: 'Culinary, nightlife, events, and unforgettable moments.' },
];
const WAVES = ['Find Your Groove','Find Your Flavor','Find Your Opportunity','Find Your Moves','Find Your Balance','Find Your Masterpiece','Find Your Playground','Find Your Getaway','Find Your Wave'];
const CHANNELS = [
  { title: 'Social Media', items: ['Instagram Stories','Facebook Ads','Video Content','Organic Social'] },
  { title: 'Digital & SEM', items: ['Google Search','Display Network','Native Ads','Website Banners'] },
  { title: 'Out-of-Home', items: ['Street Pole Banners','Bus Shelters','NYC Wallscape','Print Ads'] },
  { title: 'Video & Photo', items: ['Campaign Video','Lifestyle Photography','Drone Footage','Editorial Shoots'] },
];
const PHASES = ['Phase One: Feb–Jun','Phase Two: Jul–Oct','Phase Three: Nov–Dec'];
const METRICS = [
  { value: '65.8M+', label: 'Campaign Impressions' },
  { value: '6.5M+', label: 'Video Plays' },
  { value: '251,431', label: 'Digital Link Clicks' },
  { value: '18,995', label: 'Hotel Bookings' },
];

export default function FindYourWavePage() {
  const [activeSection, setActiveSection] = useState('fyw-overview');
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  useEffect(() => {
    const obs = new IntersectionObserver(entries => { entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); }); }, { rootMargin: '-40% 0px -40% 0px' });
    NAV.forEach(s => { const el = document.getElementById(s.id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);

  return (
    <div className="pt-32" data-testid="find-your-wave-page">
      <StickyNav sections={NAV} activeId={activeSection} />

      {/* ═══ HERO ═══ */}
      <section ref={heroRef} className="relative min-h-[85vh] flex items-center justify-center overflow-hidden bg-foreground">
        <motion.div style={{ y: heroY }} className="absolute inset-0 z-0">
          <img src="/images/fyw-img-1.jpeg" alt="Find Your Wave" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground via-foreground/50 to-foreground/30" />
        </motion.div>
        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeInUp} className="text-xs tracking-[0.4em] uppercase text-butter mb-4">City of Miami Beach</motion.p>
            <motion.h1 variants={fadeInUp} className="font-serif text-5xl sm:text-6xl lg:text-8xl font-medium text-background leading-[1.05] mb-6">Find Your Wave</motion.h1>
            <motion.div variants={fadeInUp} className="flex items-center justify-center gap-3 mb-8 flex-wrap">
              {['Destination Marketing','Creative Direction','Integrated Campaign'].map((s,i) => <span key={i} className="text-xs tracking-widest uppercase text-background/50">{i>0 && <span className="mr-3">•</span>}{s}</span>)}
            </motion.div>
            <motion.p variants={fadeInUp} className="text-background/60 text-base md:text-lg max-w-2xl mx-auto leading-relaxed mb-12">
              Repositioning Miami Beach beyond the beach through an integrated destination marketing campaign.
            </motion.p>
            <motion.a variants={fadeInUp} href="#fyw-overview" className="inline-flex items-center gap-2 text-butter text-sm tracking-widest uppercase hover:text-butter-dark transition-colors">
              Explore the Campaign <motion.span animate={{ y: [0,6,0] }} transition={{ duration: 2, repeat: Infinity }}><ArrowDown className="h-4 w-4" /></motion.span>
            </motion.a>
          </motion.div>
        </motion.div>
      </section>

      {/* ═══ OVERVIEW ═══ */}
      <section id="fyw-overview" className="py-28 md:py-36">
        <div className="container-main max-w-5xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={stagger}>
            <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-muted-foreground mb-6">Overview</motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight mb-10">
              More than a beach. An entire world to discover.
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-3xl">
              The Find Your Wave campaign was created to reposition Miami Beach as far more than a beach destination by highlighting its arts and culture, culinary scene, wellness experiences, business community, architecture and year-round lifestyle. The objective was to diversify tourism, strengthen brand perception and encourage residents, visitors and meeting planners to experience every dimension of Miami Beach.
            </motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mt-16">
            <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-muted-foreground mb-6">Services</motion.p>
            <div className="flex flex-wrap gap-3">
              {SERVICES.map((s,i) => <motion.span key={s} initial={{ opacity:0, scale:0.8 }} whileInView={{ opacity:1, scale:1 }} viewport={{ once:true }} transition={{ duration:0.4, delay:i*0.04 }} className="px-5 py-2.5 text-xs tracking-widest uppercase border border-border rounded-full hover:bg-butter hover:border-butter transition-colors duration-300 cursor-default">{s}</motion.span>)}
            </div>
          </motion.div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ═══ STRATEGY ═══ */}
      <section id="fyw-strategy" className="py-28 md:py-36">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-20 max-w-3xl mx-auto">
            <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-muted-foreground mb-4">The Strategy</motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight mb-8">Changing perception through storytelling.</motion.h2>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Research identified an opportunity to broaden public perception of Miami Beach beyond beaches alone. The campaign centered around four key brand pillars.
            </motion.p>
          </motion.div>

          {/* Four Pillars */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto mb-20">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div key={p.title} initial={{ opacity:0, y:30 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }} transition={{ delay:i*0.1, duration:0.6 }}
                  className="p-8 border border-border text-center hover:border-butter/50 transition-all duration-500 group">
                  <div className="w-14 h-14 rounded-full bg-butter/20 flex items-center justify-center mx-auto mb-5 group-hover:bg-butter transition-colors duration-300">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-serif text-lg font-medium mb-2">{p.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{p.desc}</p>
                </motion.div>
              );
            })}
          </div>

          {/* Campaign Phases */}
          <div className="max-w-4xl mx-auto">
            <motion.p initial={{ opacity:0 }} whileInView={{ opacity:1 }} viewport={{ once:true }} className="text-xs tracking-widest uppercase text-muted-foreground mb-8 text-center">Campaign Phases</motion.p>
            <div className="relative">
              <div className="hidden md:block absolute top-6 left-0 right-0 h-px bg-border" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {PHASES.map((phase, i) => <TimelineStep key={phase} step={phase} index={i} total={PHASES.length} />)}
              </div>
            </div>
            <motion.p initial={{ opacity:0 }} whileInView={{ opacity:1 }} viewport={{ once:true }} className="text-muted-foreground text-sm text-center mt-8">
              Always-on media strategy with ongoing optimization using campaign performance data.
            </motion.p>
          </div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ═══ CAMPAIGN CONCEPT ═══ */}
      <section id="fyw-concept" className="py-28 md:py-36 bg-secondary">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16 max-w-3xl mx-auto">
            <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-muted-foreground mb-4">Campaign Concept</motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight mb-6">Find Your Wave</motion.h2>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Rather than promoting a destination, the campaign invited audiences to discover their own version of Miami Beach through personalized experiences.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {WAVES.map((wave, i) => (
              <motion.div key={wave} initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }} transition={{ delay:i*0.06 }}
                className={`p-6 border border-border bg-background text-center hover:border-butter/50 hover:bg-butter/5 transition-all duration-300 ${wave === 'Find Your Wave' ? 'md:col-span-3 bg-foreground text-background border-foreground hover:bg-foreground/90' : ''}`}>
                <span className={`font-serif text-lg md:text-xl font-medium ${wave === 'Find Your Wave' ? 'text-butter' : ''}`}>{wave}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ EXECUTION ═══ */}
      <section id="fyw-execution" className="py-28 md:py-36">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mb-16">
            <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-muted-foreground mb-4">Creative Execution</motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium mb-6">Every channel. One unified campaign.</motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mb-20">
            {CHANNELS.map((ch, i) => (
              <motion.div key={ch.title} initial={{ opacity:0, y:30 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }} transition={{ delay:i*0.1, duration:0.6 }}
                className="p-8 bg-secondary border border-border hover:border-butter/50 transition-all duration-500 group">
                <h3 className="font-serif text-lg font-medium mb-5 group-hover:text-butter-dark transition-colors duration-300">{ch.title}</h3>
                <ul className="space-y-2.5">{ch.items.map(item => <li key={item} className="text-sm text-muted-foreground flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-butter flex-shrink-0" />{item}</li>)}</ul>
              </motion.div>
            ))}
          </div>

          {/* NYC Wallscape */}
          <div className="max-w-5xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="p-10 md:p-14 bg-foreground text-background">
              <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-background/40 mb-4">National Extension</motion.p>
              <motion.h3 variants={fadeInUp} className="font-serif text-2xl md:text-3xl font-medium text-background mb-4">Taking Miami Beach to Times Square.</motion.h3>
              <motion.p variants={fadeInUp} className="text-background/50 text-base leading-relaxed max-w-2xl mb-8">
                The campaign extended nationally with a high-impact digital wallscape in New York City, bringing Miami Beach's multidimensional identity to one of the world's most visible advertising platforms.
              </motion.p>
              {/* NYC wallscape image */}
              <motion.div variants={fadeInUp} className="overflow-hidden">
                <img src="/images/fyw-img-28.jpeg" alt="NYC Wallscape" className="w-full h-auto object-cover" />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ═══ RESULTS ═══ */}
      <section id="fyw-results" className="py-28 md:py-36 bg-foreground text-background">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-20">
            <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-background/40 mb-4">Campaign Results</motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-background">Measurable destination impact.</motion.h2>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {METRICS.map((m, i) => (
              <motion.div key={m.label} initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }} transition={{ delay:i*0.12, duration:0.6 }} className="text-center p-6">
                <div className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight mb-2 text-butter"><AnimNum value={m.value} /></div>
                <p className="text-sm tracking-widest uppercase text-background/40">{m.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ GALLERY ═══ */}
      <section id="fyw-gallery" className="py-28 md:py-36">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mb-12">
            <motion.p variants={fadeInUp} className="text-xs tracking-widest uppercase text-muted-foreground mb-4">Campaign Gallery</motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium">Selected campaign assets.</motion.h2>
          </motion.div>
          <GallerySlideshow />
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ═══ WHY IT MATTERS ═══ */}
      <section className="py-28 md:py-36">
        <div className="container-main text-center max-w-4xl mx-auto">
          <motion.div initial={{ opacity:0, scale:0.95 }} whileInView={{ opacity:1, scale:1 }} viewport={{ once:true }} transition={{ duration:0.8 }}>
            <p className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight italic mb-10">
              "Destination marketing is about changing perception — not just driving awareness."
            </p>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
              The Find Your Wave campaign demonstrates Muse Social Creative's ability to combine research, branding, storytelling, creative direction and integrated media planning into a unified campaign that reshaped how audiences experience a destination. From strategic positioning to multi-channel execution, every touchpoint reinforced a richer, more multidimensional vision of Miami Beach.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* ═══ NEXT ═══ */}
      <section id="fyw-next" className="py-28 md:py-36 bg-foreground">
        <div className="container-main text-center">
          <Link to="/case-studies/city-of-miami-beach" className="group block max-w-4xl mx-auto">
            <p className="text-xs tracking-widest uppercase text-background/40 mb-4">Next Project</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-6xl font-medium text-background group-hover:text-butter transition-colors duration-500 mb-4">City of Miami Beach</h2>
            <p className="text-background/50 text-sm mb-8">Spring Break Public Awareness Campaign</p>
            <span className="inline-flex items-center gap-2 bg-butter text-foreground px-8 py-3 rounded-full text-xs tracking-widest uppercase font-medium hover:bg-butter-dark transition-colors duration-300">View More</span>
          </Link>
        </div>
      </section>
    </div>
  );
}
